/**
 * Helpers purs — affichage fidélité BonSpot sur fiche client.
 */

export type LoyaltyPassRewardStatusKey =
  | 'loyaltyPass.clientDetail.status.active'
  | 'loyaltyPass.clientDetail.status.used'
  | 'loyaltyPass.clientDetail.status.expired'
  | 'loyaltyPass.clientDetail.status.cancelled'
  | 'loyaltyPass.clientDetail.status.unknown';

export type LoyaltyPassRuleProgressRow = {
  ruleId: string;
  name: string;
  type: string;
  current: number;
  threshold: number;
  rewardLabel: string;
};

export function loyaltyPassRewardStatusI18nKey(
  status: string | undefined | null
): LoyaltyPassRewardStatusKey {
  switch (String(status || '').toLowerCase()) {
    case 'active':
      return 'loyaltyPass.clientDetail.status.active';
    case 'used':
    case 'redeemed':
      return 'loyaltyPass.clientDetail.status.used';
    case 'expired':
      return 'loyaltyPass.clientDetail.status.expired';
    case 'cancelled':
      return 'loyaltyPass.clientDetail.status.cancelled';
    default:
      return 'loyaltyPass.clientDetail.status.unknown';
  }
}

export function loyaltyPassRewardStatusColor(
  status: string | undefined | null
): string {
  switch (String(status || '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'used':
    case 'redeemed':
      return 'default';
    case 'expired':
      return 'warning';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
}

export function loyaltyPassProgressPercent(
  stampCount: number | undefined | null,
  visitsRequired: number | undefined | null
): number {
  const required = Number(visitsRequired) || 0;
  if (required <= 0) return 0;
  const current = Math.max(0, Number(stampCount) || 0);
  return Math.min(100, Math.round((current / required) * 100));
}

/**
 * Seuil 1 = remise dès le ticket qualifiant (pas de barre de progression utile).
 */
export function isLoyaltyPassImmediateThreshold(
  threshold: number | undefined | null
): boolean {
  return Math.max(0, Math.floor(Number(threshold) || 0)) <= 1;
}

/** Mode multi-règles : progressByRule non vide → masquer le tampon global V1. */
export function hasLoyaltyPassRulesProgress(
  progressByRule: LoyaltyPassRuleProgressRow[] | undefined | null
): boolean {
  return Array.isArray(progressByRule) && progressByRule.length > 0;
}

/** Clé i18n pour le type de règle (legacy category_count → visit_count). */
export function loyaltyPassRuleTypeI18nKey(
  type: string | undefined | null
): 'loyaltyPass.rules.type.visit_count' | 'loyaltyPass.rules.type.ticket_multi_category' {
  return type === 'ticket_multi_category'
    ? 'loyaltyPass.rules.type.ticket_multi_category'
    : 'loyaltyPass.rules.type.visit_count';
}

/**
 * Reconstruit progressByRule après un recordVisit (compteurs BS).
 * Si `previous` fourni, conserve name/type/threshold/rewardLabel.
 */
export function mergeLoyaltyPassProgressAfterVisit(input: {
  previous?: LoyaltyPassRuleProgressRow[] | null;
  ruleCounters?: Record<string, number> | null;
  progressByRule?: LoyaltyPassRuleProgressRow[] | null;
}): LoyaltyPassRuleProgressRow[] | undefined {
  if (input.progressByRule && input.progressByRule.length > 0) {
    return input.progressByRule.map((row) => ({
      ...row,
      current:
        typeof input.ruleCounters?.[row.ruleId] === 'number'
          ? Math.max(0, Math.floor(input.ruleCounters[row.ruleId]!))
          : row.current,
    }));
  }
  if (!input.previous?.length || !input.ruleCounters) return input.previous ?? undefined;
  return input.previous.map((row) => ({
    ...row,
    current:
      typeof input.ruleCounters![row.ruleId] === 'number'
        ? Math.max(0, Math.floor(input.ruleCounters![row.ruleId]!))
        : row.current,
  }));
}
